import { supabaseClient } from '@/configs/supabase'
import moment from 'moment-timezone'
import {
  ICustomerSalesData,
  IPaymentSummaryData,
  IExpensesByType,
  IExpenseItem,
  IDailySalesFilter,
  ICreateDailyExpenseInput,
  IUpdateDailyExpenseInput,
  IUpdateCustomerPaymentInput,
  IDailySalesExportData,
  ICreateDailyCustomerSummaryInput,
  IUpdateDailyCustomerSummaryInput,
  PaymentMethod
} from './dailySalesSummary.interface'
import { getPaymentEntriesByDailyCustomerSummary } from './paymentEntry'

const THAILAND_TIMEZONE = 'Asia/Bangkok'

const handleError = (error: any, message: string) => {
  console.error(message, error)
  return null
}

// Helper function to get Thailand timezone date range
const getThailandDateRange = (dateString: string) => {
  // dateString format: YYYY-MM-DD
  const date = moment.tz(dateString, 'YYYY-MM-DD', THAILAND_TIMEZONE)
  const startOfDay = date.startOf('day').toISOString()
  const endOfDay = date.endOf('day').toISOString()
  
  return { startOfDay, endOfDay }
}

// Get customer sales data based on DailyCustomerSummary
export const getCustomerSalesData = async (
  dateString: string
): Promise<ICustomerSalesData[] | null> => {
  try {
    console.log(`📅 Getting customer sales data for date: ${dateString}`)
    
    // Get all customers (both REGULAR and GENERAL)
    const { data: allCustomers, error: customersError } = await supabaseClient
      .from('Customer')
      .select('*')
      .in('type', ['REGULAR', 'GENERAL'])
      .order('type', { ascending: true }) // GENERAL first, then REGULAR
      .order('ranking', { ascending: true })
      .order('name', { ascending: true })

    if (customersError) {
      return handleError(customersError, 'Error fetching customers:')
    }

    // Get existing daily customer summaries for this date
    const { data: existingSummaries, error: summariesError } = await supabaseClient
      .from('DailyCustomerSummary')
      .select(`
        *,
        Customer!customer_id(*)
      `)
      .eq('date', dateString)

    if (summariesError) {
      return handleError(summariesError, 'Error fetching daily customer summaries:')
    }

    // Create a map of existing summaries by customer ID
    const summariesMap = new Map()
    existingSummaries?.forEach(summary => {
      summariesMap.set(summary.customer_id, summary)
    })

    // Also get orders for this date to aggregate data
    const { startOfDay, endOfDay } = getThailandDateRange(dateString)
    const { data: orders, error: ordersError } = await supabaseClient
      .from('OrderBook')
      .select(`
        *,
        Customer!customer_id(*)
      `)
      .in('monitor_number', [1, 2])
      .in('status', ['OPEN', 'COMPLETED'])
      .gte('monitor_number_update_at', startOfDay)
      .lte('monitor_number_update_at', endOfDay)

    if (ordersError) {
      return handleError(ordersError, 'Error fetching orders:')
    }
    // Aggregate orders by customer
    const ordersByCustomer = new Map()
    orders?.forEach(order => {
      const customerId = order.customer_id
      if (!ordersByCustomer.has(customerId)) {
        ordersByCustomer.set(customerId, [])
      }
      ordersByCustomer.get(customerId).push(order)
    })

    // Create customer sales data - restructured approach
    const customerSalesData: ICustomerSalesData[] = []

    // First, add all orders as individual rows
    orders?.forEach((order: any) => {
      const customer = allCustomers?.find(c => c.id === order.customer_id)
      if (!customer) return // Skip if customer not found

      const existingSummary = summariesMap.get(customer.id)
      const totalQuantity = 
        (order.large_ice_cube_amount || 0) +
        (order.small_ice_cube_amount || 0) +
        (order.crushed_ice_amount || 0) +
        (order.coarsely_crushed_ice_amount || 0)

      const orderRow: ICustomerSalesData = {
        customerId: customer.id,
        customerName: customer.name,
        customerType: customer.type,
        customerRanking: customer.ranking,
        largeIceCubeAmount: order.large_ice_cube_amount || 0,
        smallIceCubeAmount: order.small_ice_cube_amount || 0,
        crushedIceAmount: order.crushed_ice_amount || 0,
        coarselyCrushedIceAmount: order.coarsely_crushed_ice_amount || 0,
        totalQuantity,
        totalAmountTHB: order.total_amount_thb || 0,
        // Payment info from OrderBook (per order)
        amountPaidTHB: order.amount_paid_thb || 0,
        paymentStatus: order.payment_status || 'UNPAID',
        outstandingBalanceTHB: order.outstanding_balance_thb || 0,
        orderCount: 1, // This row represents one order
        paymentCount: existingSummary?.payment_count || 0,
        lastOrderTime: order.completed_at,
        dailyCustomerSummaryId: existingSummary?.id,
        orderId: order.id,
        rowType: 'ORDER', // Mark as order row
        isVoidedFromSales: order.is_voided_from_sales || false,
        paymentEntries: [] // Will be populated later
      }

      customerSalesData.push(orderRow)
    })

    // Then, add DailyCustomerSummary records as payment-only rows (ONLY for REGULAR customers)
    const regularCustomers = allCustomers?.filter(c => c.type === 'REGULAR' && c?.deleted_at == null) || []
    
    for (const customer of regularCustomers) {
      const existingSummary = summariesMap.get(customer.id)
      
      // Add payment-only row if DailyCustomerSummary exists
      if (existingSummary) {
        const paymentOnlyRow: ICustomerSalesData = {
          customerId: customer.id,
          customerName: customer.name,
          customerType: customer.type,
          customerRanking: customer.ranking,
          largeIceCubeAmount: 0,
          smallIceCubeAmount: 0,
          crushedIceAmount: 0,
          coarselyCrushedIceAmount: 0,
          totalQuantity: 0,
          totalAmountTHB: 0,
          // Payment info from DailyCustomerSummary
          amountPaidTHB: existingSummary.amount_paid_thb || 0,
          paymentStatus: 'UNPAID',
          outstandingBalanceTHB: existingSummary.outstanding_balance_thb || 0,
          orderCount: 0,
          paymentCount: existingSummary.payment_count || 0,
          lastOrderTime: undefined,
          dailyCustomerSummaryId: existingSummary.id,
          orderId: undefined,
          rowType: 'PAYMENT_ONLY', // Mark as payment-only row
          isVoidedFromSales: false,
          paymentEntries: [] // Will be populated later
        }

        customerSalesData.push(paymentOnlyRow)
      } else {
        // Add empty payment-only row for REGULAR customers without orders and without DailyCustomerSummary
        const hasOrders = ordersByCustomer.has(customer.id)
        if (!hasOrders) {
          const emptyPaymentRow: ICustomerSalesData = {
            customerId: customer.id,
            customerName: customer.name,
            customerType: customer.type,
            customerRanking: customer.ranking,
            largeIceCubeAmount: 0,
            smallIceCubeAmount: 0,
            crushedIceAmount: 0,
            coarselyCrushedIceAmount: 0,
            totalQuantity: 0,
            totalAmountTHB: 0,
            // Default payment info
            amountPaidTHB: 0,
            paymentStatus: 'UNPAID',
            outstandingBalanceTHB: 0,
            orderCount: 0,
            paymentCount: 0,
            lastOrderTime: undefined,
            dailyCustomerSummaryId: undefined,
            orderId: undefined,
            rowType: 'PAYMENT_ONLY', // Mark as payment-only row
            isVoidedFromSales: false,
            paymentEntries: [] // Will be populated later
          }

          customerSalesData.push(emptyPaymentRow)
        }
      }
    }

    // Sort by customer ranking, then by order ID
    customerSalesData.sort((a, b) => {
      // First sort by ranking
      const rankingA = a.customerRanking || 999
      const rankingB = b.customerRanking || 999
      if (rankingA !== rankingB) {
        return rankingA - rankingB
      }
      
      // Then by customer name
      if (a.customerName !== b.customerName) {
        return a.customerName.localeCompare(b.customerName)
      }
      
      // Finally by order ID (orders first, then payment-only)
      if (a.orderId && b.orderId) {
        return a.orderId - b.orderId
      } else if (a.orderId && !b.orderId) {
        return -1 // Orders before payment-only
      } else if (!a.orderId && b.orderId) {
        return 1 // Payment-only after orders
      }
      return 0
    })

    // Fetch PaymentEntry records for each DailyCustomerSummary
    const uniqueSummaryIds = new Set<string>()
    customerSalesData.forEach(row => {
      if (row.dailyCustomerSummaryId) {
        uniqueSummaryIds.add(row.dailyCustomerSummaryId)
      }
    })

    const paymentEntriesMap = new Map<string, import('./dailySalesSummary.interface').IPaymentEntry[]>()
    for (const summaryId of uniqueSummaryIds) {
      const entries = await getPaymentEntriesByDailyCustomerSummary(summaryId)
      paymentEntriesMap.set(summaryId, entries)
    }

    // Populate paymentEntries field for each row
    // Distribute entries based on orderBookId:
    // - ORDER rows: entries where orderBookId matches the order's id
    // - PAYMENT_ONLY rows: entries where orderBookId is null/undefined
    // - Backward compat: entries with no orderBookId (old data) go to PAYMENT_ONLY rows,
    //   or if no PAYMENT_ONLY row exists for that customer, to the first ORDER row
    customerSalesData.forEach(row => {
      if (!row.dailyCustomerSummaryId) return
      const allEntries = paymentEntriesMap.get(row.dailyCustomerSummaryId) || []

      if (row.rowType === 'ORDER' && row.orderId != null) {
        // ORDER row: only entries explicitly linked to this order
        row.paymentEntries = allEntries.filter(e => e.orderBookId === row.orderId)
      } else {
        // PAYMENT_ONLY row: entries with no orderBookId (null/undefined)
        row.paymentEntries = allEntries.filter(e => e.orderBookId == null)
      }
    })

    console.log(`✅ Created ${customerSalesData.length} customer sales rows`)
    return customerSalesData

  } catch (error) {
    return handleError(error, 'Error in getCustomerSalesData:')
  }
}

// Calculate payment summary from customer sales data
export const calculatePaymentSummary = (
  customerSalesData: ICustomerSalesData[],
  remainingCashTHB: number = 0
): IPaymentSummaryData => {
  // Filter out voided orders before calculating
  const activeCustomerData = customerSalesData.filter(c => c.isVoidedFromSales !== true)

  const summary = {
    totalCashPayments: 0,
    totalTransferPayments: 0,
    totalOutstandingBalance: 0,
    totalPayments: 0,
    remainingCashTHB,
  }

  // Calculate per-row (not per-customer) to match Excel logic:
  // outstanding = max(0, row.totalAmountTHB - row.paidFromEntries)
  // This prevents payment on a zero-amount row from reducing outstanding of other rows.
  for (const customer of activeCustomerData) {
    let paidFromEntries = 0
    if (customer.paymentEntries && customer.paymentEntries.length > 0) {
      customer.paymentEntries.forEach(entry => {
        if (entry.paymentMethod === PaymentMethod.CASH) {
          summary.totalCashPayments += entry.amountTHB
        } else if (entry.paymentMethod === PaymentMethod.TRANSFER) {
          summary.totalTransferPayments += entry.amountTHB
        }
        paidFromEntries += entry.amountTHB
      })
    }
    summary.totalOutstandingBalance += Math.max(0, customer.totalAmountTHB - paidFromEntries)
  }

  summary.totalPayments = summary.totalCashPayments + summary.totalTransferPayments

  return summary
}

// Get or create daily sales summary
export const getOrCreateDailySalesSummary = async (dateString: string) => {
  try {
    // Try to get existing summary
    const { data: existingSummary, error: fetchError } = await supabaseClient
      .from('DailySalesSummary')
      .select('*')
      .eq('id', dateString)
      .single()

    if (fetchError && fetchError.code !== 'PGRST116') {
      return handleError(fetchError, 'Error fetching daily sales summary:')
    }

    if (existingSummary) {
      return existingSummary
    }

    // Create new summary if it doesn't exist
    const { data: newSummary, error: createError } = await supabaseClient
      .from('DailySalesSummary')
      .insert({
        id: dateString,
        remaining_cash_thb: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single()

    if (createError) {
      return handleError(createError, 'Error creating daily sales summary:')
    }

    return newSummary

  } catch (error) {
    return handleError(error, 'Error in getOrCreateDailySalesSummary:')
  }
}

// Update remaining cash
export const updateRemainingCash = async (
  dateString: string,
  remainingCashTHB: number
) => {
  try {
    // Ensure daily summary exists
    await getOrCreateDailySalesSummary(dateString)

    const { data: updatedSummary, error: updateError } = await supabaseClient
      .from('DailySalesSummary')
      .update({
        remaining_cash_thb: remainingCashTHB,
        updated_at: new Date().toISOString()
      })
      .eq('id', dateString)
      .select()
      .single()

    if (updateError) {
      return handleError(updateError, 'Error updating remaining cash:')
    }

    return updatedSummary

  } catch (error) {
    return handleError(error, 'Error in updateRemainingCash:')
  }
}

// Get or create daily customer summary
export const getOrCreateDailyCustomerSummary = async (
  date: string,
  customerId: string
) => {
  try {
    // Try to get existing summary
    const { data: existingSummary, error: fetchError } = await supabaseClient
      .from('DailyCustomerSummary')
      .select('*')
      .eq('date', date)
      .eq('customer_id', customerId)
      .single()

    if (fetchError && fetchError.code !== 'PGRST116') {
      return handleError(fetchError, 'Error fetching daily customer summary:')
    }

    if (existingSummary) {
      return existingSummary
    }

    // Create new summary if it doesn't exist (only payment data, no order totals)
    // Generate a unique ID for the record
    const recordId = `${date}_${customerId}_${Date.now()}`
    
    const { data: newSummary, error: createError } = await supabaseClient
      .from('DailyCustomerSummary')
      .insert({
        id: recordId, // Explicitly provide ID
        date,
        customer_id: customerId,
        // Include all required fields with defaults
        total_large_ice_cube_amount: 0,
        total_small_ice_cube_amount: 0,
        total_crushed_ice_amount: 0,
        total_coarsely_crushed_ice_amount: 0,
        total_amount_thb: 0,
        amount_paid_thb: 0,
        outstanding_balance_thb: 0,
        order_count: 0,
        payment_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single()

    if (createError) {
      return handleError(createError, 'Error creating daily customer summary:')
    }

    return newSummary

  } catch (error) {
    return handleError(error, 'Error in getOrCreateDailyCustomerSummary:')
  }
}

// Update daily customer summary
export const updateDailyCustomerSummary = async (
  date: string,
  customerId: string,
  updateData: IUpdateDailyCustomerSummaryInput
) => {
  try {
    console.log(`🔄 updateDailyCustomerSummary called:`, {
      date,
      customerId,
      updateData: JSON.stringify(updateData, null, 2)
    })
    
    // Ensure daily customer summary exists first
    const existingSummary = await getOrCreateDailyCustomerSummary(date, customerId)
    console.log(`📋 Existing summary:`, existingSummary)

    if (!existingSummary) {
      console.log(`❌ Failed to create/get existing summary`)
      return handleError(new Error('Failed to create daily customer summary'), 'Error creating daily customer summary:')
    }

    const updatePayload = {
      ...(updateData.amountPaidTHB !== undefined && { amount_paid_thb: updateData.amountPaidTHB }),
      ...(updateData.outstandingBalanceTHB !== undefined && { outstanding_balance_thb: updateData.outstandingBalanceTHB }),
      updated_at: new Date().toISOString()
    }
    
    console.log(`💾 Update payload:`, updatePayload)

    // Use update instead of upsert since we ensured record exists
    const { data: updatedSummary, error: updateError } = await supabaseClient
      .from('DailyCustomerSummary')
      .update(updatePayload)
      .eq('id', existingSummary.id)
      .select()
      .single()

    if (updateError) {
      console.log(`❌ Update error:`, updateError)
      return handleError(updateError, 'Error updating daily customer summary:')
    }

    console.log(`✅ Updated summary:`, updatedSummary)
    return updatedSummary

  } catch (error) {
    console.log(`💥 Exception in updateDailyCustomerSummary:`, error)
    return handleError(error, 'Error in updateDailyCustomerSummary:')
  }
}

// Update order payment information (now uses OrderBook directly)
export const updateCustomerPayments = async (
  dateString: string,
  paymentUpdates: IUpdateCustomerPaymentInput[]
) => {
  try {
    console.log(`🔄 updateCustomerPayments called with:`, {
      dateString,
      paymentUpdates: JSON.stringify(paymentUpdates, null, 2)
    })

    // Update each order's payment data directly in OrderBook
    const updatePromises = paymentUpdates.map(async (update) => {
      // Extract orderId from the update (we need to modify the interface to include orderId)
      const orderId = (update as any).orderId
      
      if (orderId) {
        // Update specific order
        console.log(`📦 Processing order: ${orderId}`)
        
        const { data: updatedOrder, error: updateError } = await supabaseClient
          .from('OrderBook')
          .update({
            amount_paid_thb: update.amountPaidTHB,
            outstanding_balance_thb: update.outstandingBalanceTHB,
            updated_at: new Date().toISOString()
          })
          .eq('id', orderId)
          .select()
          .single()

        if (updateError) {
          console.log(`❌ Error updating order ${orderId}:`, updateError)
          return null
        }

        console.log(`✅ Updated order ${orderId}:`, updatedOrder ? 'Success' : 'Failed')
        return updatedOrder
      } else {
        // Payment-only update (customer without orders) - use DailyCustomerSummary
        console.log(`👤 Processing payment-only for customer: ${update.customerId}`)
        
        const result = await updateDailyCustomerSummary(dateString, update.customerId, {
          amountPaidTHB: update.amountPaidTHB,
          outstandingBalanceTHB: update.outstandingBalanceTHB
        })
        
        console.log(`📝 Updated customer ${update.customerId}:`, result ? 'Success' : 'Failed')
        return result
      }
    })

    const results = await Promise.all(updatePromises)
    console.log(`✅ All updates completed:`, results.length)
    return true

  } catch (error) {
    return handleError(error, 'Error in updateCustomerPayments:')
  }
}

// Get expenses by date
export const getExpensesByDate = async (
  dateString: string
): Promise<IExpensesByType | null> => {
  try {
    const { data: expenses, error: expensesError } = await supabaseClient
      .from('DailyExpense')
      .select('*')
      .eq('daily_summary_id', dateString)
      .order('expense_type')
      .order('created_at')

    if (expensesError) {
      return handleError(expensesError, 'Error fetching expenses:')
    }

    // Group expenses by type
    const expensesByType: IExpensesByType = {
      FUEL: [],
      EMPLOYEE_OVERTIME: [],
      OTHER: []
    }

    expenses?.forEach((expense) => {
      const expenseItem: IExpenseItem = {
        id: expense.id,
        name: expense.name,
        amountTHB: expense.amount_thb,
        createdAt: expense.created_at,
        updatedAt: expense.updated_at
      }

      expensesByType[expense.expense_type as keyof IExpensesByType].push(expenseItem)
    })

    return expensesByType

  } catch (error) {
    return handleError(error, 'Error in getExpensesByDate:')
  }
}

// Create expense
export const createExpense = async (
  expenseInput: ICreateDailyExpenseInput
) => {
  try {
    // Ensure daily summary exists
    await getOrCreateDailySalesSummary(expenseInput.dailySummaryId)

    // Generate a unique ID for the expense (similar to cuid format)
    const expenseId = crypto.randomUUID()

    const now = new Date().toISOString()

    const { data: newExpense, error: createError } = await supabaseClient
      .from('DailyExpense')
      .insert({
        id: expenseId,
        daily_summary_id: expenseInput.dailySummaryId,
        expense_type: expenseInput.expenseType,
        name: expenseInput.name,
        amount_thb: expenseInput.amountTHB,
        created_at: now,
        updated_at: now
      })
      .select()
      .single()

    if (createError) {
      return handleError(createError, 'Error creating expense:')
    }

    return newExpense

  } catch (error) {
    return handleError(error, 'Error in createExpense:')
  }
}

// Update expense
export const updateExpense = async (
  expenseId: string,
  expenseInput: IUpdateDailyExpenseInput
) => {
  try {
    const { data: updatedExpense, error: updateError } = await supabaseClient
      .from('DailyExpense')
      .update({
        name: expenseInput.name,
        amount_thb: expenseInput.amountTHB,
        updated_at: new Date().toISOString()
      })
      .eq('id', expenseId)
      .select()
      .single()

    if (updateError) {
      return handleError(updateError, 'Error updating expense:')
    }

    return updatedExpense

  } catch (error) {
    return handleError(error, 'Error in updateExpense:')
  }
}

// Delete expense
export const deleteExpense = async (expenseId: string) => {
  try {
    const { error: deleteError } = await supabaseClient
      .from('DailyExpense')
      .delete()
      .eq('id', expenseId)

    if (deleteError) {
      return handleError(deleteError, 'Error deleting expense:')
    }

    return true

  } catch (error) {
    return handleError(error, 'Error in deleteExpense:')
  }
}

// Get complete daily sales data
export const getDailySalesData = async (
  dateString: string
): Promise<IDailySalesExportData | null> => {
  try {
    // Get customer sales data
    const customerSalesData = await getCustomerSalesData(dateString)
    if (!customerSalesData) {
      return null
    }

    // Get or create daily summary
    const dailySummary = await getOrCreateDailySalesSummary(dateString)
    if (!dailySummary) {
      return null
    }

    // Calculate payment summary
    const paymentSummary = calculatePaymentSummary(
      customerSalesData,
      dailySummary.remaining_cash_thb
    )

    // Get expenses
    const expenses = await getExpensesByDate(dateString)
    if (!expenses) {
      return null
    }

    return {
      date: dateString,
      customers: customerSalesData,
      paymentSummary,
      expenses
    }

  } catch (error) {
    return handleError(error, 'Error in getDailySalesData:')
  }
}